import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import en from './en.json'
import fr from './fr.json'
import es from './es.json'

type Bundle = Record<string, string>
type Locale = 'en' | 'fr' | 'es'

const BUNDLES: Record<Locale, Bundle> = { en, fr, es }

interface I18nContextValue {
  locale: Locale
  setLocale: (l: Locale) => void
  t: (key: string, vars?: Record<string, string | number>) => string
}

const I18nContext = createContext<I18nContextValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const initial: Locale = (() => {
    const stored = localStorage.getItem('discover.locale') as Locale | null
    if (stored && ['en', 'fr', 'es'].includes(stored)) return stored
    const nav = navigator.language.slice(0, 2)
    return (['en', 'fr', 'es'] as Locale[]).find((l) => l === nav) ?? 'en'
  })()

  const [locale, setLocaleState] = useState<Locale>(initial)

  const setLocale = (l: Locale) => {
    setLocaleState(l)
    localStorage.setItem('discover.locale', l)
    document.documentElement.lang = l
  }

  const value = useMemo<I18nContextValue>(() => ({
    locale,
    setLocale,
    t: (key, vars) => {
      const bundle = BUNDLES[locale]
      const fallback = BUNDLES.en
      let str = bundle[key] ?? fallback[key] ?? key
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v))
        }
      }
      return str
    },
  }), [locale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used inside I18nProvider')
  return ctx
}

export function useT() {
  return useI18n().t
}
